export interface CollectionInfo {
  label: string;
  link: string;
  description: string;
  nameApi: string;
  doc: string;
  showReport: boolean;
  showTree: boolean;
}

export interface ContainerInfo {
  title: string;
  subtitle: string;
  link: string;
  shortName: string;
  nameApi: string;
  titleStats: string;
  showStats: boolean;
  showContainerStatistic: boolean;
  showSearch: boolean;
}

export class VersionInfos {
  dashboardVersion = "";
  statisticsVersion = "";
  datamodelVersion = "";
  apiUrl = "";
  apiVersion = "";
  authUrl = "";
  backendVersion = "";
}

export interface ItemMetaData {
  /**
   * Version of this item
   */
  version?: string;
  /**
   * Date of creation of this scenario
   */
  date?: string;
  /**
   * Edition status of the use-case.  DRAFT - A draft item is an item that is still under construction FEEDBACK - A Feedback item is an item that cannot be validated with the actual version of the tool, or the scenario has to be modified after verification (Ready status) READY - A Ready item is an item ready to be verified VALIDATED - A validated item is an item that has been ready, verified, and validated
   */
  stateValidation?: string;
  /**
   * User that creates this item
   */
  author?: string;
  /**
   * Version of the ScenarioManager data model in which the use-case is encoded
   */
  dataModelVersion?: string;
}

/**
 * Mapping between a variable name used in a formula and the corresponding model parameter
 */
export interface VariableMapping {
  /**
   * The name as used in the formula (must contain only letters and avoid math expression such as cos, sqrt, ...
   */
  name: string;
  /**
   * The JSONPath expression for mapping (Root document is the parent #definition/Scenario)
   */
  jsonpath?: string;
}

/**
 * Scenario parameter object that can hold a value on multiple abstraction level
 */
export interface MultiLevelParam {
  /**
   * Unit in which is expressed the specified value. If not defined, the first possible unit of the mlp is used.
   */
  unit?: string;
  abstracted?: string;
  concrete?: number;
  min?: number;
  minExclusive?: boolean;
  max?: number;
  maxExclusive?: boolean;
  /**
   * The value, expressed as a number or as a math expression computable by the MXparser library. ex: 'x^2 + sin(y)', x and y must be provided in the 'variables' parameter of this object.
   */
  formula?: string;
  primitive?: string;
  /**
   * The list of variables used in the 'formula'
   */
  variables?: Array<VariableMapping>;
}

/**
 * Reference to a static object or an actor
 */
export interface ObjectReference {
  /**
   * Reference to a static object or an actor.
   */
  value?: number;
}

/**
 * Parameter Value
 */
export interface ParamValue {
  value?: MultiLevelParam;
  reference?: ObjectReference;
}

/**
 * Parameter that is specific to a given scenario
 */
export interface HighLevelParameter {
  id?: number;
  paramDescription?: ParamDescription;
  value?: ParamValue;
}

/**
 * Reference to a scenario, with a specific version and an optional comment.
 */
export interface ScenarioReference {
  id: number;
  /**
   * Version of the scenario. This information can be used to ensure that the scenario did not change since this reference was created.
   */
  version?: string;
  /**
   * Commentary linked with the creation/modification
   */
  comment?: string;
  /**
   * Representation of this model parents ([...]/grandparent/parent/this)
   */
  path?: string;
  /**
   * Name to the scenario
   */
  name?: string;
  idContainer?: number;
}

/**
 * Parameter of a use-case, used for simulation purposes
 */
export interface UseCaseParameter {
  /**
   * unique id
   */
  id?: number;
  /**
   * Type of use-case parameter  INPUT - The parameter is a variable parameter of the use-case TRIGGER - The parameter defines a trigger of a scene of the scenario EXPECTED - The parameters defines the expected behavior of the system under test
   */
  type?: any;
  /**
   * Name to the parameter
   */
  name?: string;
  /**
   * Location of the parameter inside the scenario
   */
  path: string;
  end?;
  start?;
  nominalValue?: MultiLevelParam;
  value?: MultiLevelParam;
  /**
   * Commentary linked with the creation/modification
   */
  description?: string;
  parameters?: UseCaseParameter[];
}

export interface BehaviorState {
  id: string;
  behavior: any;
  actorId: number;
  description: string;
  systemName?: string;
  values: ParamValue[];
  parameters?: any[];
  from: StartEndParameter;
  to: StartEndParameter;
  displayStartEnd?: boolean;
}

export interface StartEndParameter {
  name?: string;
  unit?: string;
  type?: string;
  viewType?: string;
  description: string;
  placeholder?: string;
  path: string;
  nominalValue: MultiLevelParam;
  value: any;
  dynamic;
  step;
  text?: string;
}

/**
 * Reference to a UseCaseParameter, with it's value
 */
export interface UseCaseParameterReference {
  /**
   * reference to a UseCaseParameter
   */
  id?: number;
  value?: MultiLevelParam | any;
}

/**
 * A combination of usecase parameters
 */
export interface UseCaseParametersSubset {
  usecaseParameterList?: Array<UseCaseParameterReference>;
}

export const initialStateUsecase = {
  tags: [],
  attachments: [],
  systems: [],
  expectedBehavior: "",
  usecaseParameters: [],
  relevantCases: [],
  traceability: { database: "", originId: "", source: "" },
  relevantSubsets: [],
  scenario: { id: null, comment: "", version: "", path: "" },
  image: "",
};

export interface AssociatedUseCase {
  tags: Array<string>;
  usecase: UseCase;
}
export interface UseCase {
  /**
   * Identifier of the use-case. ID generated by application.
   */
  id?: number;
  metadata?: ItemMetaData;
  /**
   * Name of the use-case
   */
  name: string;
  /**
   * Detailed description of the use-case
   */
  description?: string;
  /**
   * Global view of the usecase
   */
  image?: string;
  traceability?: Traceability;
  /**
   * Description of the expected behavior of a system subjected to this use-case.
   */
  attachments?: Array<Attachment>;
  tags?: Array<Tag>;
  scenario?: any;
  systems?: Array<AutomatedDrivingSystem>;
  usecaseParameters?: Array<UseCaseParameter>;
  ego: string;
  /**
   * Relevant cases for this use-case
   */
  relevantCases?: Array<ScenarioReference>;
  /**
   * A list of relevant combinations of parameters
   */
  relevantSubsets?: Array<UseCaseParametersSubset>;
  usecaseInducers?: string[];
  intendedResponses?: string[];
  expectedBehaviors?: BehaviorState[];
}

export interface Attachment {
  filename: string;
  file: string;
  description: string;
}

export interface ParamDescription {
  type: string;
  name: string;
  description: string;
  unit: string;
  abstractValues: string[];
}

export interface Reference {
  value: number;
}

export interface Tag {
  id: number;
  parent: number;
  path: string;
  attachment: Partial<Attachment>;
  name: string;
  description: string;
  sprite: string;
}

export interface Traceability {
  source: string;
  database: string;
  originId: string;
}

export interface FilterFormUsecase {
  name: string;
  scenario: any;
  system: any;
  source: any;
}

export interface FilterContainer {
  field?: string;
  operator?: string;
  value?: string | string[];
  customType?: boolean;
}

export interface FiltersContainer {
  list?: Array<FilterContainer>;
}

export interface UseCaseDatas {
  idContainer: number;
  usecases: Partial<UseCase[]>;
  permissions: any;
  totalResults: number;
}

export interface UseCaseData {
  idContainer: number;
  usecase: Partial<UseCase>;
  permissions: any;
  totalResults: number;
}

/**
 * Autonomous driving system
 */
export interface AutomatedDrivingSystem {
  /**
   * Identifier of the system
   */
  id?: number;
  /**
   * Identifier of the parent
   */
  parent?: number;
  /**
   * Representation of this model parents ([...]/grandparent/parent/this)
   */
  path?: string;
  /**
   * Name of the system
   */
  name: string;
  /**
   * Description and remarks about this system
   */
  description?: string;
  /**
   * Picture illustrating the system
   */
  image?: string;

  metadata?: ItemMetaData;
  /**
   * Representation of the system on an overview
   */
  sprite?: string;
  attachment?: Attachment;
  parameters?: Array<ParamDescription>;
  odd?: ODD;
  behaviors?: Array<any>;
  associatedUsecases: Array<AssociatedUseCase>;
}

export interface HomeContainerData {
  containerInfo: ContainerInfo;
  containers: any[];
  err: string;
}

export interface HomeStatisticData {
  containerInfo: ContainerInfo;
  pieChartData: any;
  pieChartLabels: any[];
  errorStat: string;
}

export enum ViewType {
  STRING = "string",
  NUMBER = "number",
  TEXT = "text",
  ENUM = "enum",
  BOOLEAN = "boolean",
  OBJECT = "object",
  MULTILEVEL = "multilevel",
  FILE = "file",
  REF = "ref",
  ARRAY = "array",
  ENUMS = "enums",
  CUSTOM_ENUM = "custom-enum",
  X_RECOMMENDED = "x-recommended",
}

/**
 * Operational design domain of system
 */
export interface ODD {
  /**
   * unique id
   */
  id?: number;
  /**
   * Name of the ODD
   */
  name: string;
  /**
   * The description of the ODD
   */
  description?: string;
  categories: Array<ODDCategory>;
}

/**
 * Category of ODD parameters
 */
export interface ODDCategory {
  /**
   * unique id
   */
  id?: number;
  /**
   * Name of the ODD
   */
  name: string;
  /**
   * The description of the ODD
   */
  description?: string;
  subCategories?: Array<ODDCategory>;
  parameters: Array<ODDParameter>;
  color?: string;
}

/**
 * Parameter of the ODD
 */
export interface ODDParameter {
  /**
   * unique id
   */
  id?: number;
  /**
   * Name of the ODD
   */
  name?: string;
  /**
   * The description of the ODD
   */
  description?: string;
  /**
   * Qualifier of the ODD parameter  INCLUDED - The value of the ODD parameter refers to items included in the ODD EXCLUDED - The value of the ODD parameter refers to items excluded of the ODD CONDITIONAL - The value of the ODD parameter refers to items included in the ODD under certain conditions
   */
  qualifier: Qualifier;
  /**
   * Location of the parameter inside the scenario model
   */
  path: string;
  value?: MultiLevelParam;
  remarks?: string;
}

/**
 * Qualifier of an ODD parameter
 */
export enum Qualifier {
  INCLUDED = "INCLUDED",
  EXCLUDED = "EXCLUDED",
  CONDITIONAL = "CONDITIONAL",
}

/**
 * Mode of an ODD paramater
 */
export enum Modes {
  PERMISSIVE = "PERMISSIVE",
  RESTRICTIVE = "RESTRICTIVE",
  DEFAULT = "DEFAULT"
}